// The live map panel's leaves. One tone per state, used the same way
// everywhere — the count tile's icon, the row's pill, the avatar's dot and the
// map pin all wear it — so a colour means one thing across the whole screen.
// Depth comes from white cards on the glass frame; hierarchy from weight.

import type { Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@bednbody/ui/components/avatar";
import { Badge, type BadgeVariant } from "@bednbody/ui/components/badge";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@bednbody/ui/components/empty";
import { cn } from "@bednbody/ui/lib/utils";

export type Tone = "danger" | "warning" | "info" | "success" | "gold" | "primary" | "muted";

export const TONE_DOT: Record<Tone, string> = {
  danger: "bg-destructive",
  warning: "bg-warning",
  info: "bg-info",
  success: "bg-success",
  gold: "bg-gold",
  primary: "bg-primary",
  muted: "bg-muted-foreground/50",
};

const TONE_BADGE: Record<Tone, BadgeVariant> = {
  danger: "destructive",
  warning: "warning",
  info: "info",
  success: "success",
  gold: "gold-soft",
  primary: "primary",
  muted: "muted",
};

/**
 * A count that is also a filter. Boxy and bordered, not a soft rounded pill:
 * uppercase label, a small NEUTRAL icon in its own bordered square, then the
 * number. Colour appears in exactly two places — the number when something is
 * wrong, and the tile itself when it is the active filter.
 */
export function StatTileButton({
  label,
  value,
  hint,
  icon: IconComponent,
  image,
  alarm,
  wide = false,
  pressed,
  onPress,
}: {
  label: string;
  value: number;
  hint: string;
  icon?: Icon;
  /** A branded badge in place of the glyph (the calendar's tiles). */
  image?: string;
  /** Turns the number red while it is above zero — the one tile allowed to shout. */
  alarm?: boolean;
  /** Spans both columns, so a fifth count sits under the grid without breaking it. */
  wide?: boolean;
  pressed: boolean;
  onPress: () => void;
}) {
  const shouting = alarm === true && value > 0;
  const numberClass = cn(
    "font-bold leading-none tracking-tight tabular-nums",
    shouting ? "text-destructive" : "text-foreground",
  );
  // A filled glyph like the sidebar nav, with no box of its own — the bezel
  // already frames it, and a box inside a box inside a box is the clutter.
  const glyph = image ? (
    <img alt="" aria-hidden="true" className="size-6 shrink-0 object-contain" src={image} />
  ) : IconComponent ? (
    <IconComponent
      className={cn("size-6 shrink-0", pressed ? "text-primary" : "text-muted-foreground")}
      weight="fill"
    />
  ) : null;
  const labelText = (
    <span className="truncate text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      {label}
    </span>
  );
  // A BEZEL, not a flat box: a white rim with its own border and a soft
  // shadow, holding an inner panel with a second border. That double edge is
  // what reads as "a card with a bit of border" rather than a plain rectangle.
  return (
    <button
      aria-pressed={pressed}
      className={cn(
        "group min-w-0 rounded-xl border bg-card p-1 text-left shadow-sm transition",
        wide ? "col-span-2" : null,
        pressed ? "border-primary/40" : "border-border hover:border-primary/30",
      )}
      onClick={onPress}
      type="button"
    >
      <span
        className={cn(
          "flex h-full rounded-lg border transition-colors",
          // The wide tile is a SLIM ROW, not a fifth square: it counts the
          // calm state, so it takes less room than the four that need you.
          wide ? "items-center gap-2.5 px-3 py-2" : "flex-col gap-2.5 p-3",
          pressed
            ? "border-primary/25 bg-primary/10"
            : "border-border/70 bg-muted/40 group-hover:bg-muted/60",
        )}
      >
        {wide ? (
          <>
            {glyph}
            {labelText}
            <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">· {hint}</span>
            <span className={cn(numberClass, "text-xl")}>{value}</span>
          </>
        ) : (
          <>
            <span className="flex items-center justify-between gap-2">
              {labelText}
              {glyph}
            </span>
            <span className="flex items-baseline gap-2">
              <span className={cn(numberClass, "text-2xl")}>{value}</span>
              <span className="truncate text-xs text-muted-foreground">{hint}</span>
            </span>
          </>
        )}
      </span>
    </button>
  );
}

/**
 * The status pill — the ONE place a state is allowed to carry colour, so the
 * icons and type around it can stay neutral.
 */
export function StatusPill({ label, tone }: { label: string; tone: Tone }) {
  return (
    <Badge
      className="h-6 max-w-60 gap-1.5 rounded-md px-2 font-semibold"
      variant={TONE_BADGE[tone]}
    >
      <span className={cn("size-1.5 shrink-0 rounded-full", TONE_DOT[tone])} />
      <span className="truncate">{label}</span>
    </Badge>
  );
}

export function SectionLabel({ label, count }: { label: string; count: number }) {
  return (
    // A band, not a floating caption: it sticks while its group scrolls, and
    // its own fill separates one group from the next without extra gaps.
    <h3 className="sticky top-0 z-10 flex items-center gap-2 border-y border-border/60 bg-muted/70 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground backdrop-blur-sm first:border-t-0">
      {label}
      <span className="rounded bg-card px-1.5 tabular-nums text-foreground">{count}</span>
    </h3>
  );
}

/** A group's rows, divided — they already sit on the panel's white sheet. */
export function ListCard({ children }: { children: ReactNode }) {
  return <ul className="divide-y divide-border/70">{children}</ul>;
}

/** Initials — neutral; the row's pill carries the state. */
/** A face when there is one (0279), otherwise the initials. */
export function InitialsAvatar({ name, photo }: { name: string; photo?: string | null }) {
  const initials =
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?";
  return (
    <Avatar className="rounded-md" size="lg">
      {photo ? <AvatarImage alt="" className="rounded-md object-cover" src={photo} /> : null}
      <AvatarFallback className="rounded-md bg-muted text-xs font-bold text-foreground">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

/** A filled, neutral glyph like the sidebar nav — no box. The row's pill carries urgency. */
export function IconSquare({ icon: IconComponent }: { icon: Icon }) {
  return (
    <span className="flex size-9 shrink-0 items-center justify-center text-muted-foreground">
      <IconComponent className="size-5" weight="fill" />
    </span>
  );
}

/** A booking's scheduled time as its leading mark — the fact a schedule is read by. */
export function TimeBlock({ time }: { time: string }) {
  const [clock, period] = time.split(" ");
  return (
    // Plain type in a fixed column, not a cramped box: read down the list,
    // the times line up like a schedule.
    <span className="flex w-12 shrink-0 flex-col leading-none">
      <span className="text-sm font-bold tabular-nums text-foreground">{clock}</span>
      {period ? (
        <span className="mt-1 text-xs font-medium text-muted-foreground">{period}</span>
      ) : null}
    </span>
  );
}

/** One row. The head is the select target; details and actions open beneath it. */
export function ListRow({
  leading,
  title,
  subtitle,
  trailing,
  meta,
  selected = false,
  onSelect,
  rowId,
  children,
}: {
  leading: ReactNode;
  title: string;
  subtitle: string;
  trailing?: ReactNode;
  meta?: ReactNode;
  selected?: boolean;
  onSelect: () => void;
  /** Lets a list find this row again, e.g. to scroll back to it. */
  rowId?: string;
  children?: ReactNode;
}) {
  return (
    <li className={cn("relative", selected ? "bg-primary/5" : null)} data-row-id={rowId}>
      {selected ? <span className="absolute inset-y-0 left-0 w-0.5 bg-primary" /> : null}
      <button
        aria-expanded={children ? selected : undefined}
        className="flex w-full items-center gap-4 px-4 py-3.5 text-left transition-colors hover:bg-muted/50"
        onClick={onSelect}
        type="button"
      >
        {leading}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-foreground">{title}</span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">{subtitle}</span>
        </span>
        <span className="flex shrink-0 flex-col items-end gap-1">
          {trailing}
          {meta ? (
            <span className="text-xs font-medium tabular-nums text-muted-foreground">{meta}</span>
          ) : null}
        </span>
      </button>
      {selected && children ? <div className="space-y-3 px-4 pb-4">{children}</div> : null}
    </li>
  );
}

export function DetailGrid({ items }: { items: readonly { label: string; value: string }[] }) {
  return (
    <dl className="grid grid-cols-[repeat(auto-fit,minmax(7rem,1fr))] gap-x-4 gap-y-3 rounded-lg bg-muted/40 px-3 py-2.5">
      {items.map((item) => (
        <div className="min-w-0" key={item.label}>
          <dt className="text-xs text-muted-foreground">{item.label}</dt>
          <dd className="truncate text-sm font-semibold text-foreground" title={item.value}>
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function PanelEmpty({
  icon: IconComponent,
  title,
  description,
}: {
  icon: Icon;
  title: string;
  description: string;
}) {
  return (
    <Empty className="border-0 p-10">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <IconComponent weight="duotone" />
        </EmptyMedia>
        <EmptyTitle className="text-base font-semibold">{title}</EmptyTitle>
        <EmptyDescription>{description}</EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}
